import { useEffect, useState } from "react";
import { supabase } from "@/lib/supabase";

interface Member {
  id: string;
  full_name: string;
  email: string;
  status: string;
  merged_into_id?: string | number | null;
}

interface Toast {
  id: number;
  type: "success" | "error" | "info";
  message: string;
}

type RecipientFilter = "all" | "pending" | "approved" | "active" | "rejected";

export default function EmailManager() {
  const [members, setMembers] = useState<Member[]>([]);
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [toasts, setToasts] = useState<Toast[]>([]);
  const [subject, setSubject] = useState("");
  const [content, setContent] = useState("");
  const [recipientFilter, setRecipientFilter] = useState<RecipientFilter>("all");
  const [previewOpen, setPreviewOpen] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);

  let toastCounter = 0;
  function showToast(type: "success" | "error" | "info", message: string) {
    const id = Date.now() + toastCounter++;
    setToasts((prev) => [...prev, { id, type, message }]);
    setTimeout(() => {
      setToasts((prev) => prev.filter((t) => t.id !== id));
    }, 8000);
  }

  useEffect(() => {
    loadMembers();
  }, []);

  async function loadMembers() {
    setLoading(true);
    const { data, error } = await supabase
      .from("members")
      .select("id, full_name, email, status, merged_into_id")
      .order("created_at", { ascending: false });
    if (!error && data) {
      const live = (data as Member[]).filter(
        (m) => m.status !== "merged" && m.merged_into_id == null,
      );
      setMembers(live);
    }
    setLoading(false);
  }

  const filteredMembers =
    recipientFilter === "all"
      ? members
      : members.filter((m) => m.status === recipientFilter);

  const validRecipients = filteredMembers.filter((m) => m.email && m.email.includes("@"));

  function getPreviewHtml(): string {
    const bodyHtml = (content || "<em style='color: #8B7355;'>Digite o conteúdo do email para visualizar...</em>").replace(/\n/g, "<br>").replace(/\{\{name\}\}/g, "<strong style='color:#C67B5C;'>Maria</strong>");
    return `
      <div style="font-family: Georgia, serif; max-width: 560px; margin: 0 auto; padding: 40px 24px; background: #FDF5EC;">
        <div style="text-align: center; padding-bottom: 32px; border-bottom: 2px solid #E8D5C0;">
          <h1 style="color: #5C3A21; font-size: 24px; margin: 0 0 8px;">Entre Elas</h1>
          <p style="color: #C67B5C; font-size: 14px; letter-spacing: 4px; text-transform: uppercase; margin: 0;">F&eacute;, apoio &amp; prop&oacute;sito</p>
        </div>
        <div style="padding: 32px 0;">
          <h2 style="color: #5C3A21; font-size: 20px; margin: 0 0 20px;">${subject || "Assunto do email"}</h2>
          <div style="color: #5C3A21; font-size: 15px; line-height: 1.7; margin: 0;">
            ${bodyHtml}
          </div>
        </div>
        <div style="padding-top: 24px; border-top: 1px solid #E8D5C0; text-align: center;">
          <p style="color: #8B7355; font-size: 12px; margin: 0;">
            Entre Elas &mdash; Uma rede de mulheres que se apoiam, crescem e transformam umas as outras.
          </p>
          <p style="color: #8B7355; font-size: 11px; margin: 6px 0 0;">
            Para deixar de receber nossos emails, responda a esta mensagem com &quot;REMOVER&quot;.
          </p>
        </div>
      </div>
    `;
  }

  async function handleSend() {
    if (!subject.trim()) {
      showToast("error", "Preencha o assunto do email.");
      return;
    }
    if (!content.trim()) {
      showToast("error", "Preencha o conteúdo do email.");
      return;
    }
    if (validRecipients.length === 0) {
      showToast("error", "Nenhuma destinatária com email válido neste grupo.");
      return;
    }

    setSending(true);
    setShowConfirm(false);

    try {
      const recipients = validRecipients.map((m) => ({
        email: m.email,
        name: m.full_name || "Querida",
      }));

      // Envia apenas conteúdo puro — a edge function aplica o template
      const { data, error: fnError } = await supabase.functions.invoke("send-mass-email", {
        body: {
          recipients,
          subject,
          content,
        },
      });

      if (fnError || !data || !data.success) {
        showToast("error", `Falha ao enviar emails: ${fnError?.message || data?.error || "Erro desconhecido"}`);
      } else {
        showToast("success", `Emails enviados! ${data.sent} enviados, ${data.failed || 0} falhas.`);
        setSubject("");
        setContent("");
      }
    } catch (err: any) {
      showToast("error", `Erro ao enviar: ${err?.message || "Erro inesperado"}`);
    } finally {
      setSending(false);
    }
  }

  const filterOptions: { key: RecipientFilter; label: string; count: number }[] = [
    { key: "all", label: "Todas", count: members.length },
    { key: "pending", label: "Pendentes", count: members.filter((m) => m.status === "pending").length },
    { key: "approved", label: "Aprovadas", count: members.filter((m) => m.status === "approved").length },
    { key: "active", label: "Ativas", count: members.filter((m) => m.status === "active").length },
    { key: "rejected", label: "Rejeitadas", count: members.filter((m) => m.status === "rejected").length },
  ];

  return (
    <div>
      {/* Toasts */}
      {toasts.length > 0 && (
        <div className="fixed top-20 right-6 z-50 flex flex-col gap-2 max-w-sm">
          {toasts.map((t) => (
            <div
              key={t.id}
              className={`px-4 py-3 rounded-xl shadow-lg font-body text-sm flex items-start gap-2.5 animate-slide-in ${
                t.type === "success"
                  ? "bg-green-50 text-green-800 border border-green-200"
                  : t.type === "error"
                  ? "bg-red-50 text-red-800 border border-red-200"
                  : "bg-blue-50 text-blue-800 border border-blue-200"
              }`}
            >
              <i
                className={`${
                  t.type === "success"
                    ? "ri-checkbox-circle-line text-green-500"
                    : t.type === "error"
                    ? "ri-error-warning-line text-red-500"
                    : "ri-information-line text-blue-500"
                } mt-0.5`}
              />
              <span className="flex-1">{t.message}</span>
              <button
                onClick={() => setToasts((prev) => prev.filter((x) => x.id !== t.id))}
                className="text-brown/30 hover:text-brown/60 cursor-pointer shrink-0"
              >
                <i className="ri-close-line" />
              </button>
            </div>
          ))}
        </div>
      )}

      {/* Header */}
      <div className="mb-6">
        <h3 className="font-display text-xl text-brown">Email Marketing</h3>
        <p className="font-body text-sm text-brown/60 mt-1">
          Envie emails para todas as cadastradas de uma só vez
        </p>
      </div>

      {loading ? (
        <div className="text-center py-12">
          <div className="w-8 h-8 border-2 border-primary/20 border-t-primary rounded-full animate-spin mx-auto mb-3" />
          <p className="font-body text-sm text-brown/60">Carregando cadastradas...</p>
        </div>
      ) : (
        <div className="space-y-5">
          {/* Recipient filter */}
          <div className="bg-white rounded-2xl p-5 md:p-6">
            <label className="font-body text-xs text-brown/60 uppercase tracking-wide block mb-3">
              Destinatárias
            </label>
            <div className="flex flex-wrap gap-2 mb-4">
              {filterOptions.map((opt) => (
                <button
                  key={opt.key}
                  onClick={() => setRecipientFilter(opt.key)}
                  className={`flex items-center gap-2 px-4 py-2 rounded-full font-body text-sm transition-all duration-200 cursor-pointer whitespace-nowrap ${
                    recipientFilter === opt.key
                      ? "bg-primary text-white shadow-md"
                      : "bg-beige/50 text-brown hover:bg-beige border border-nude/60"
                  }`}
                >
                  <span>{opt.label}</span>
                  <span
                    className={`font-display text-sm ${
                      recipientFilter === opt.key ? "text-white/80" : "text-brown/40"
                    }`}
                  >
                    {opt.count}
                  </span>
                </button>
              ))}
            </div>
            <div className="flex items-center gap-2 bg-green-50 text-green-800 px-4 py-2.5 rounded-lg font-body text-sm">
              <i className="ri-mail-check-line text-green-600" />
              <span>
                <strong>{validRecipients.length}</strong> emails válidos serão enviados para{" "}
                <strong>{recipientFilter === "all" ? "todas as cadastradas" : filterOptions.find((f) => f.key === recipientFilter)?.label.toLowerCase()}</strong>
              </span>
            </div>
          </div>

          {/* Email editor */}
          <div className="bg-white rounded-2xl p-5 md:p-6 space-y-4">
            <div>
              <label className="font-body text-xs text-brown/60 uppercase tracking-wide block mb-1.5">
                Assunto *
              </label>
              <input
                type="text"
                value={subject}
                onChange={(e) => setSubject(e.target.value)}
                className="w-full px-4 py-2.5 rounded-lg border border-nude bg-beige/50 font-body text-sm text-brown focus:outline-none focus:border-primary transition-colors"
                placeholder="Ex: Novo encontro do Entre Elas — Reserve sua vaga!"
              />
            </div>
            <div>
              <label className="font-body text-xs text-brown/60 uppercase tracking-wide block mb-1.5">
                Conteúdo do email *
              </label>
              <p className="font-body text-xs text-brown/50 mb-2">
                Use <strong>{"{{name}}"}</strong> para personalizar com o nome de cada cadastrada.
              </p>
              <textarea
                value={content}
                onChange={(e) => setContent(e.target.value)}
                rows={10}
                maxLength={5000}
                className="w-full px-4 py-2.5 rounded-lg border border-nude bg-beige/50 font-body text-sm text-brown focus:outline-none focus:border-primary transition-colors resize-none"
                placeholder={`Olá {{name}},

Temos uma novidade incrível para você!

Nosso próximo encontro do Entre Elas será no dia 15 de julho. Será um momento especial de conexão, partilha e crescimento juntas.

Esperamos você lá!

Com carinho,
Equipe Entre Elas`}
              />
              <p className="font-body text-[11px] text-brown/40 mt-1 text-right">
                {content.length}/5000
              </p>
            </div>

            {/* Action buttons */}
            <div className="flex flex-wrap gap-3 pt-2">
              <button
                onClick={() => setPreviewOpen(!previewOpen)}
                className="flex items-center gap-2 px-5 py-2.5 rounded-full bg-beige border border-nude text-brown font-body text-sm hover:bg-white transition-colors whitespace-nowrap cursor-pointer"
              >
                <i className={previewOpen ? "ri-eye-off-line" : "ri-eye-line"} />
                {previewOpen ? "Fechar preview" : "Preview"}
              </button>
              <button
                onClick={() => setShowConfirm(true)}
                disabled={!subject.trim() || !content.trim() || validRecipients.length === 0}
                className="flex items-center gap-2 px-6 py-2.5 rounded-full bg-primary text-white font-body text-sm hover:bg-primary/90 transition-colors disabled:opacity-40 disabled:cursor-not-allowed whitespace-nowrap cursor-pointer"
              >
                <i className="ri-send-plane-line" />
                Enviar para {validRecipients.length} cadastrada{validRecipients.length !== 1 ? "s" : ""}
              </button>
            </div>
          </div>

          {/* Preview */}
          {previewOpen && (
            <div className="bg-white rounded-2xl p-5 md:p-6">
              <div className="flex items-center gap-2 mb-4">
                <i className="ri-mail-open-line text-primary text-lg" />
                <h4 className="font-display text-base text-brown">Preview do email</h4>
              </div>
              <div
                className="rounded-xl overflow-hidden border border-nude"
                dangerouslySetInnerHTML={{ __html: getPreviewHtml() }}
              />
            </div>
          )}

          {/* Confirm modal */}
          {showConfirm && (
            <div className="fixed inset-0 z-[60] bg-black/50 flex items-center justify-center px-4">
              <div className="bg-white rounded-2xl p-6 max-w-md w-full">
                <div className="text-center mb-6">
                  <div className="w-12 h-12 flex items-center justify-center rounded-full bg-primary/10 mx-auto mb-3">
                    <i className="ri-send-plane-line text-primary text-xl" />
                  </div>
                  <h4 className="font-display text-lg text-brown mb-2">Confirmar envio</h4>
                  <p className="font-body text-sm text-brown/70">
                    Você está prestes a enviar <strong>{validRecipients.length}</strong> email
                    {validRecipients.length !== 1 ? "s" : ""} para{" "}
                    <strong>
                      {recipientFilter === "all"
                        ? "todas as cadastradas"
                        : filterOptions.find((f) => f.key === recipientFilter)?.label.toLowerCase()}
                    </strong>
                    .
                  </p>
                  <p className="font-body text-xs text-brown/50 mt-2">
                    Assunto: <em>{subject}</em>
                  </p>
                </div>
                <div className="flex gap-3">
                  <button
                    onClick={() => setShowConfirm(false)}
                    className="flex-1 px-4 py-2.5 rounded-full bg-white border border-nude text-brown font-body text-sm hover:bg-beige transition-colors cursor-pointer"
                  >
                    Cancelar
                  </button>
                  <button
                    onClick={handleSend}
                    disabled={sending}
                    className="flex-1 flex items-center justify-center gap-2 px-4 py-2.5 rounded-full bg-primary text-white font-body text-sm hover:bg-primary/90 transition-colors disabled:opacity-60 cursor-pointer"
                  >
                    {sending ? (
                      <>
                        <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                        Enviando...
                      </>
                    ) : (
                      <>
                        <i className="ri-send-plane-line" />
                        Confirmar envio
                      </>
                    )}
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Sending overlay */}
          {sending && (
            <div className="fixed inset-0 z-[70] bg-black/40 flex items-center justify-center">
              <div className="bg-white rounded-2xl p-8 flex flex-col items-center gap-4">
                <div className="w-10 h-10 border-2 border-primary/20 border-t-primary rounded-full animate-spin" />
                <p className="font-body text-sm text-brown">Enviando emails...</p>
                <p className="font-body text-xs text-brown/50">Isso pode levar alguns segundos</p>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}